import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  MoreVert,
  Person,
  CalendarToday,
  TrendingUp,
} from '@mui/icons-material';

export const GameCard = ({ game, onJoin, onView, onEdit, showActions = true }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            {game?.title || game?.name || 'Untitled Game'}
          </Typography>
          <Chip 
            label={game?.status || 'Unknown'} 
            color={getStatusColor(game?.status)}
            size="small"
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {game?.participantsCount || 0} / {game?.maxParticipants || 0} Players
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {game?.startDate ? new Date(game.startDate).toLocaleDateString() : 'No Date'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Entry Fee: ₹{game?.entryFee || 0}
          </Typography>
        </Box>
      </CardContent>
      
      {showActions && (
        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Button size="small" onClick={() => onView?.(game)}>
            View Details
          </Button>
          <Box>
            {onJoin && (
              <Button size="small" variant="contained" onClick={() => onJoin(game)}>
                Join Game
              </Button>
            )}
            {onEdit && (
              <IconButton size="small" onClick={() => onEdit(game)}>
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export const PlayerCard = ({ player, onSelect, selected = false }) => {
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        cursor: onSelect ? 'pointer' : 'default',
      }}
      onClick={() => onSelect?.(player)}
    >
      <CardContent sx={{ flex: 1, textAlign: 'center' }}>
        <Avatar
          src={player?.imageUrl}
          sx={{ width: 60, height: 60, mx: 'auto', mb: 2 }}
        >
          {player?.name?.charAt(0)}
        </Avatar>
        
        <Typography variant="h6" component="h3" gutterBottom>
          {player?.name || 'Unknown Player'}
        </Typography>
        
        <Chip 
          label={player?.role || 'Unknown'} 
          color="primary"
          size="small"
          sx={{ mb: 1 }}
        />
        
        <Typography variant="body2" color="text.secondary">
          Team: {player?.team || 'Unknown'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          Points: {player?.points || 0}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const TeamCard = ({ team, onView, showActions = true }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, textAlign: 'center' }}>
        <Avatar
          src={team?.logoUrl}
          sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
        >
          {team?.name?.charAt(0)}
        </Avatar>
        
        <Typography variant="h6" component="h2" gutterBottom>
          {team?.name || 'Unknown Team'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {team?.description || 'No description available'}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Box textAlign="center">
            <Typography variant="h6" color="primary">
              {team?.playersCount || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Players
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" color="primary">
              {team?.matchesPlayed || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Matches
            </Typography>
          </Box>
        </Box>
      </CardContent>
      
      {showActions && (
        <CardActions sx={{ justifyContent: 'center' }}>
          <Button size="small" onClick={() => onView?.(team)}>
            View Team
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export const SeriesCard = ({ series, onView, onCreateGame, showActions = true }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {series?.name || 'Unknown Series'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {series?.description || 'No description available'}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarToday sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {series?.startDate ? new Date(series.startDate).toLocaleDateString() : 'No Date'} - {series?.endDate ? new Date(series.endDate).toLocaleDateString() : 'No Date'}
          </Typography>
        </Box>
        
        <Chip 
          label={series?.status || 'Unknown'} 
          color={getStatusColor(series?.status)}
          size="small"
        />
      </CardContent>
      
      {showActions && (
        <CardActions sx={{ justifyContent: 'space-between' }}>
          <Button size="small" onClick={() => onView?.(series)}>
            View Details
          </Button>
          {onCreateGame && (
            <Button size="small" variant="contained" onClick={() => onCreateGame(series)}>
              Create Game
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'ongoing':
      return 'success';
    case 'pending':
    case 'upcoming':
      return 'warning';
    case 'completed':
    case 'finished':
      return 'info';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};